import { WEBHOOK_EVENTS, formatDateTime, type WebhookEventKey } from '@erp/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Plus, Webhook } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '../../components/ui/alert.tsx';
import { Badge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Card } from '../../components/ui/card.tsx';
import { Dialog } from '../../components/ui/dialog.tsx';
import { Field, Input } from '../../components/ui/field.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { api, ApiError, errorMessage } from '../../lib/api.ts';
import { useSession } from '../../lib/session.ts';
import { useToast } from '../../lib/toast.tsx';

// Mirrors apps/api/src/integrations/webhooks.service.ts.
interface WebhookView {
  id: string;
  url: string;
  description: string | null;
  events: WebhookEventKey[];
  secretPrefix: string;
  active: boolean;
  disabledReason: 'manual' | 'failures' | null;
  consecutiveFailures: number;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  createdAt: string;
}

interface DeliveryView {
  id: string;
  event: string;
  status: 'pending' | 'succeeded' | 'failed';
  attempts: number;
  nextAttemptAt: string | null;
  lastStatusCode: number | null;
  lastError: string | null;
  createdAt: string;
  deliveredAt: string | null;
}

const KEY = ['webhooks'] as const;
const EVENTS = Object.keys(WEBHOOK_EVENTS) as WebhookEventKey[];
const eventLabel = (event: string) =>
  event in WEBHOOK_EVENTS ? WEBHOOK_EVENTS[event as WebhookEventKey].label : 'Prueba';

const issueMessage = (e: unknown) =>
  e instanceof ApiError && e.issues.length > 0
    ? (e.issues[0]?.message ?? errorMessage(e))
    : errorMessage(e);

type Open =
  | { kind: 'create' }
  | { kind: 'edit'; webhook: WebhookView }
  | { kind: 'rotate'; webhook: WebhookView }
  | { kind: 'deliveries'; webhook: WebhookView }
  | { kind: 'delete'; webhook: WebhookView };

/**
 * Webhooks (HU-WEB-14): the ERP tells the website (the WordPress plugin) or n8n that
 * something happened. Signed with a secret shown only once; ids and codes only.
 */
export function WebhooksPage() {
  const webhooks = useQuery({ queryKey: KEY, queryFn: () => api<WebhookView[]>('/webhooks') });
  const { data: session } = useSession();
  const tz = session?.tenant.timezone;
  const client = useQueryClient();
  const notify = useToast();
  const [open, setOpen] = useState<Open | null>(null);
  const close = () => {
    setOpen(null);
  };

  const test = async (webhook: WebhookView) => {
    try {
      await api(`/webhooks/${webhook.id}/test`, { method: 'POST', body: {} });
      await client.invalidateQueries({ queryKey: [...KEY, webhook.id] });
      notify('Prueba en camino: se envía en menos de un minuto.', 'success');
    } catch (e) {
      notify(errorMessage(e), 'error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Webhooks</h1>
          <p className="text-sm text-muted-foreground">
            Avisos a otros sistemas cuando algo pasa (el sitio web, n8n). Solo llevan códigos e
            identificadores, nunca datos de personas, y van firmados.
          </p>
        </div>
        <Button
          variant="brand"
          onClick={() => {
            setOpen({ kind: 'create' });
          }}
        >
          <Plus /> Nuevo webhook
        </Button>
      </div>
      {webhooks.isPending ? (
        <Loading />
      ) : webhooks.error ? (
        <Alert tone="danger">{errorMessage(webhooks.error)}</Alert>
      ) : webhooks.data.length === 0 ? (
        <Card className="text-center text-sm text-muted-foreground">Todavía no hay webhooks.</Card>
      ) : (
        <Card className="p-0">
          <ul className="divide-y divide-border">
            {webhooks.data.map((w) => (
              <li key={w.id} className="space-y-3 p-4">
                <div className="min-w-0 space-y-1 text-sm">
                  <p className="flex flex-wrap items-center gap-2">
                    <Webhook className="size-4 text-muted-foreground" aria-hidden="true" />
                    <span className="font-medium">{w.description ?? 'Webhook'}</span>
                    {w.active ? (
                      <Badge tone="success">Activo</Badge>
                    ) : (
                      <Badge tone={w.disabledReason === 'failures' ? 'danger' : 'neutral'}>
                        {w.disabledReason === 'failures' ? 'Desactivado por fallos' : 'Desactivado'}
                      </Badge>
                    )}
                  </p>
                  <p className="break-all font-mono text-muted-foreground">{w.url}</p>
                  <p className="text-muted-foreground">
                    {w.events.map((e) => WEBHOOK_EVENTS[e].label).join(' · ')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Secreto <span className="font-mono">{w.secretPrefix}…</span> ·{' '}
                    {w.lastSuccessAt
                      ? `última entrega el ${formatDateTime(w.lastSuccessAt, tz)}`
                      : 'sin entregas todavía'}
                    {w.consecutiveFailures > 0
                      ? ` · ${String(w.consecutiveFailures)} envíos fallidos seguidos`
                      : ''}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!w.active}
                    onClick={() => void test(w)}
                  >
                    Enviar prueba
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setOpen({ kind: 'deliveries', webhook: w });
                    }}
                  >
                    Ver envíos
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setOpen({ kind: 'edit', webhook: w });
                    }}
                  >
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setOpen({ kind: 'rotate', webhook: w });
                    }}
                  >
                    Cambiar secreto
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setOpen({ kind: 'delete', webhook: w });
                    }}
                  >
                    Borrar
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
      {open?.kind === 'create' && <FormDialog onClose={close} />}
      {open?.kind === 'edit' && <FormDialog webhook={open.webhook} onClose={close} />}
      {open?.kind === 'rotate' && <RotateDialog webhook={open.webhook} onClose={close} />}
      {open?.kind === 'deliveries' && <DeliveriesDialog webhook={open.webhook} onClose={close} />}
      {open?.kind === 'delete' && <DeleteDialog webhook={open.webhook} onClose={close} />}
    </div>
  );
}

function FormDialog({ webhook, onClose }: { webhook?: WebhookView; onClose: () => void }) {
  const client = useQueryClient();
  const notify = useToast();
  const [url, setUrl] = useState(webhook?.url ?? '');
  const [description, setDescription] = useState(webhook?.description ?? '');
  const [events, setEvents] = useState<WebhookEventKey[]>(webhook?.events ?? []);
  const [active, setActive] = useState(webhook?.active ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (webhook) {
        await api(`/webhooks/${webhook.id}`, {
          method: 'PATCH',
          body: { url, description, events, active },
        });
        notify('Webhook guardado.', 'success');
        onClose();
      } else {
        const result = await api<{ secret: string }>('/webhooks', {
          method: 'POST',
          body: { url, description, events },
        });
        setSecret(result.secret);
      }
      await client.invalidateQueries({ queryKey: KEY });
    } catch (e) {
      setError(issueMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (secret) return <SecretDialog title="Webhook creado" secret={secret} onClose={onClose} />;
  return (
    <Dialog open onClose={onClose} title={webhook ? 'Editar webhook' : 'Nuevo webhook'}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <Field
          label="Dirección (URL)"
          hint="Debe empezar con https:// y ser pública. La da el sitio o la herramienta que recibe los avisos."
        >
          {(a11y) => (
            <Input
              {...a11y}
              type="url"
              required
              maxLength={500}
              placeholder="https://remaxpro-sv.com/wp-json/…"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
              }}
            />
          )}
        </Field>
        <Field label="Descripción" hint="Para reconocerlo: «Sitio web», «n8n»…">
          {(a11y) => (
            <Input
              {...a11y}
              maxLength={80}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
              }}
            />
          )}
        </Field>
        <fieldset className="space-y-1">
          <legend className="mb-1 text-sm font-medium">Qué avisa</legend>
          {EVENTS.map((event) => (
            <label key={event} className="flex min-h-11 items-start gap-3 text-sm">
              <input
                type="checkbox"
                className="mt-0.5 size-5"
                checked={events.includes(event)}
                onChange={(e) => {
                  setEvents(
                    e.target.checked ? [...events, event] : events.filter((x) => x !== event),
                  );
                }}
              />
              <span>
                <span className="font-medium">{WEBHOOK_EVENTS[event].label}</span>
                <span className="block text-muted-foreground">{WEBHOOK_EVENTS[event].help}</span>
              </span>
            </label>
          ))}
        </fieldset>
        {webhook && (
          <label className="flex min-h-11 items-center gap-3 text-sm">
            <input
              type="checkbox"
              className="size-5"
              checked={active}
              onChange={(e) => {
                setActive(e.target.checked);
              }}
            />
            Activo
          </label>
        )}
        {error && <Alert tone="danger">{error}</Alert>}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" variant="brand" loading={saving} disabled={events.length === 0}>
            {webhook ? 'Guardar' : 'Crear webhook'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** The signing secret, shown only now. */
function SecretDialog({
  title,
  secret,
  onClose,
}: {
  title: string;
  secret: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <Dialog open onClose={onClose} title={title}>
      <div className="space-y-4">
        <Alert tone="warning" title="Copia el secreto ahora">
          Es la única vez que se muestra. Con él, quien recibe los avisos comprueba que vienen del
          ERP. Si lo pierdes, cámbialo.
        </Alert>
        <Field label="Secreto de firma">
          {(a11y) => (
            <Input
              {...a11y}
              readOnly
              value={secret}
              className="font-mono"
              onFocus={(e) => {
                e.currentTarget.select();
              }}
            />
          )}
        </Field>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose}>
            Listo
          </Button>
          <Button
            variant="brand"
            onClick={() =>
              void navigator.clipboard.writeText(secret).then(() => {
                setCopied(true);
              })
            }
          >
            <Copy /> {copied ? 'Copiado' : 'Copiar'}
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          En WordPress va en <span className="font-mono">wp-config.php</span>, junto a la clave de
          API.
        </p>
      </div>
    </Dialog>
  );
}

function RotateDialog({ webhook, onClose }: { webhook: WebhookView; onClose: () => void }) {
  const client = useQueryClient();
  const notify = useToast();
  const [busy, setBusy] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const rotate = async () => {
    setBusy(true);
    try {
      const result = await api<{ secret: string }>(`/webhooks/${webhook.id}/rotate-secret`, {
        method: 'POST',
        body: {},
      });
      setSecret(result.secret);
      await client.invalidateQueries({ queryKey: KEY });
    } catch (e) {
      notify(errorMessage(e), 'error');
    } finally {
      setBusy(false);
    }
  };
  if (secret) return <SecretDialog title="Secreto nuevo" secret={secret} onClose={onClose} />;
  return (
    <Dialog
      open
      onClose={onClose}
      title="Cambiar secreto"
      description="El secreto actual deja de firmar al instante: actualiza el que tiene el sitio o la herramienta, o sus avisos se rechazarán."
    >
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="danger" loading={busy} onClick={() => void rotate()}>
          Cambiar secreto
        </Button>
      </div>
    </Dialog>
  );
}

const STATUS: Record<
  DeliveryView['status'],
  { label: string; tone: 'success' | 'warning' | 'danger' }
> = {
  succeeded: { label: 'Entregado', tone: 'success' },
  pending: { label: 'Pendiente', tone: 'warning' },
  failed: { label: 'Falló', tone: 'danger' },
};

function DeliveriesDialog({ webhook, onClose }: { webhook: WebhookView; onClose: () => void }) {
  const { data: session } = useSession();
  const tz = session?.tenant.timezone;
  const deliveries = useQuery({
    queryKey: [...KEY, webhook.id],
    queryFn: () => api<DeliveryView[]>(`/webhooks/${webhook.id}/deliveries`),
    refetchInterval: 15_000,
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title="Envíos recientes"
      description="Los últimos 50, de los últimos 30 días."
    >
      {deliveries.isPending ? (
        <Loading />
      ) : deliveries.error ? (
        <Alert tone="danger">{errorMessage(deliveries.error)}</Alert>
      ) : deliveries.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">Todavía no hay envíos.</p>
      ) : (
        <ul className="divide-y divide-border text-sm">
          {deliveries.data.map((d) => (
            <li key={d.id} className="space-y-1 py-2">
              <p className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{eventLabel(d.event)}</span>
                <Badge tone={STATUS[d.status].tone}>{STATUS[d.status].label}</Badge>
              </p>
              <p className="text-xs text-muted-foreground">
                {formatDateTime(d.createdAt, tz)} · {d.attempts}{' '}
                {d.attempts === 1 ? 'intento' : 'intentos'}
                {d.lastError ? ` · ${d.lastError}` : ''}
                {d.nextAttemptAt && d.attempts > 0
                  ? ` · reintento el ${formatDateTime(d.nextAttemptAt, tz)}`
                  : ''}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}

function DeleteDialog({ webhook, onClose }: { webhook: WebhookView; onClose: () => void }) {
  const client = useQueryClient();
  const notify = useToast();
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      await api(`/webhooks/${webhook.id}`, { method: 'DELETE' });
      await client.invalidateQueries({ queryKey: KEY });
      notify('Webhook borrado.', 'success');
      onClose();
    } catch (e) {
      notify(errorMessage(e), 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title="Borrar webhook"
      description={`Deja de avisar a ${webhook.url} y se borran sus envíos pendientes.`}
    >
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="danger" loading={busy} onClick={() => void remove()}>
          Borrar
        </Button>
      </div>
    </Dialog>
  );
}
